import React, { useEffect, useState, useRef } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaFilter, FaDownload } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import UserFilters from '../../components/commonComponent/UserFilters';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Eventindex = () => {
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    cluster: '',
    chapter: '',
    startDate: '',
    endDate: ''
  });

  const [dropdowns, setDropdowns] = useState({
    clusters: [],
    chapters: []
  });

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  const qrRef = useRef();

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    fetchEvents(currentPage, searchQuery, filters);
  }, [currentPage, searchQuery, filters]);

  const handleDownloadQRCode = async () => {
    if (!qrRef.current) return;
    // Use html2canvas to capture the QR code div
    const canvas = await html2canvas(qrRef.current, { backgroundColor: '#fff', scale: 1 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [250, 320] });
    // Add QR image
    pdf.addImage(imgData, 'PNG', 65, 40, 150, 100);
    // Add event name below QR
    if (selectedEvent?.Name) {
      pdf.setFontSize(14);
      pdf.text(selectedEvent.Name, 125, 230, { align: 'center' });
    }
    pdf.save(`${selectedEvent?.Name || 'QRCode'}.pdf`);
  };

  const handleDirectDownloadQR = async (event) => {
    try {
      // Create a temporary div to render QR code for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = '#fff';
      tempDiv.style.padding = '20px';
      tempDiv.style.width = '200px';
      tempDiv.style.height = '200px';
      document.body.appendChild(tempDiv);

      // Create QR code in the temp div using qrcode.react
      const { createRoot } = require('react-dom/client');
      const root = createRoot(tempDiv);
      
      await new Promise((resolve) => {
        root.render(
          React.createElement(QRCodeSVG, {
            value: event._id,
            size: 180,
            level: "M",
            includeMargin: true
          })
        );
        setTimeout(resolve, 100); // Wait for render
      });

      // Use html2canvas to capture the QR code
      const canvas = await html2canvas(tempDiv, { backgroundColor: '#fff', scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF using same format as modal
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [250, 320] });
      pdf.addImage(imgData, 'PNG', 35, 40, 180, 180);
      
      // Add event name
      if (event.name) {
        pdf.setFontSize(14);
        pdf.text(event.name, 125, 240, { align: 'center' });
      }
      
      // Add date and time info
      pdf.setFontSize(10);
      pdf.text(`Date: ${new Date(event.startDate).toLocaleDateString()}`, 125, 260, { align: 'center' });
      pdf.text(`Time: ${new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, 125, 275, { align: 'center' });
      
      pdf.save(`${event.name || 'Event'}_QRCode.pdf`);
      
      // Clean up
      root.unmount();
      document.body.removeChild(tempDiv);
      toastSuccess('QR Code PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating QR PDF:', error);
      toastError('Failed to download QR Code PDF');
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const [clustersRes, chaptersRes] = await Promise.all([
        axios.get('/admin/api/clusters/all'),
        axios.get('/admin/api/chapters/all'),
      ]);

      setDropdowns({
        clusters: clustersRes.data?.cluster?.map(c => ({ value: c._id, label: c.name })) || [],
        chapters: chaptersRes.data?.chapter?.map(ch => ({ value: ch._id, label: ch.name })) || [],
      });
    } catch (err) {
      toastError('Failed to load filter dropdowns');
      console.error(err);
    }
  };

  const fetchEvents = async (page = 1, search = '', filtersData = filters) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        search,
        cluster: filtersData.cluster || '',
        chapter: filtersData.chapter || '',
        startDate: filtersData.startDate || '',
        endDate: filtersData.endDate || '',
      });

      const res = await axios.get(`/api/event?${params.toString()}`);
      const { events = [], totalCount = 0 } = res.data;
      setEvents(events);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch events');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/event/${id}`);
      toastSuccess('Event deleted successfully!');
      setCurrentPage(1);
      fetchEvents(1, searchQuery, filters);
    } catch (err) {
      toastError('Failed to delete event');
      console.error(err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      cluster: '',
      chapter: '',
      startDate: '',
      endDate: ''
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Open modal and set selected event
  const openQrModal = (event) => {
    setSelectedEvent(event);
    setQrModalOpen(true);
  };

  // Close modal
  const closeQrModal = () => {
    setQrModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <div className="container mt-2">
      {/* Header and Search */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark"> events</h3>
        <div className="d-flex align-items-center">
          <input
            type="text"
            className="form-control w-75"
            placeholder="Search Event..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button
            className="btn btn-sm ms-2 d-flex align-items-center gap-2"
            style={{
              backgroundColor: showFilters ? '#C44C23' : 'transparent',
              color: showFilters ? '#fff' : '#C44C23',
              border: '1px solid #C44C23',
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <UserFilters
          filters={filters}
          dropdowns={dropdowns}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          filtersToShow={['cluster', 'chapter', 'startDate', 'endDate']}
        />
      )}

      {/* Events Table */}
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Events</h5>
          <Link to="/event/create" className="btn btn-sm"
            style={{ backgroundColor: '#C44C23', color: '#fff' }}>
            + Add New Event
          </Link>
        </div>

        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ fontSize: '14px', lineHeight: '2' }}
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2">Sr no.</th>
                <th className="py-1 px-2">Meeting Id</th>
                <th className="py-1 px-2">Chapter</th>
                <th className="py-1 px-2">Event Name</th>
                <th className="py-1 px-2">Event Date</th>
                <th className="py-1 px-2">Event Time</th>
                <th className="py-1 px-2">Venue</th>
                <th className="py-1 px-2">Qr Code</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length > 0 ? (
                events.map((e, idx) => (
                  <tr key={e._id}>
                    <td>{(currentPage - 1) * limit + idx + 1}</td>
                    <td>{e.eventId || '-'}</td>
                    <td>{e.chapter?.map(ch => ch.name).join(', ') || 'N/A'}</td>
                    <td>{e.name}</td>
                    <td>{new Date(e.startDate).toLocaleDateString()}</td>
                    <td>{new Date(e.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{e.venue?.name || e.meetingVenue?.name || 'N/A'}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <span style={{ cursor: 'pointer' }} onClick={() => openQrModal(e)} title="View QR Code">
                          <QRCodeSVG
                            value={e._id}
                            size={25}
                            level="M"
                            includeMargin={false}
                          />
                        </span>
                        <button
                          className="btn btn-sm p-0"
                          style={{
                            fontSize: '12px',
                            color: '#C44C23',
                            border: 'none',
                            backgroundColor: 'transparent'
                          }}
                          onClick={() => handleDirectDownloadQR(e)}
                          title="Download QR Code PDF"
                        >
                          <FaDownload size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <Link to={`/event/view/${e._id}`}
                        className="btn btn-sm me-1 py-0 px-2"
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.2',
                          boxShadow: 'none',
                          color: '#C44C23',
                          border: 'none'
                        }}
                        title="View"
                      >
                        <FaEye size={14} />
                      </Link>
                      <Link
                        to={`/event/edit/${e._id}`}
                        className="btn btn-sm me-1 py-0 px-2"
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.2',
                          boxShadow: 'none',
                          color: '#C44C23',
                          border: 'none'
                        }}
                        title="Edit"
                      >
                        <FaEdit size={14} />
                      </Link>
                      <button
                        className="btn btn-sm me-1 py-0 px-2"
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.2',
                          boxShadow: 'none',
                          color: '#C44C23',
                          border: 'none'
                        }}
                        title="Delete"
                        onClick={() => handleDelete(e._id)}
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">No event found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #C44C23'
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalOpen && selectedEvent && (
      <div
        className="modal fade show"
        style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1050,
        }}
        onClick={closeQrModal}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '400px' }}
        >
          <div className="modal-content p-3">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">Event QR Code</h5>
              <button className="btn btn-sm btn-primary" onClick={handleDownloadQRCode}>
                Download
              </button>
            </div>

            {/* Body */}
            <div className="modal-body text-center">
              <h6 className="mb-3">{selectedEvent.Name}</h6>

             <div ref={qrRef}>
              <QRCodeSVG
                value={selectedEvent._id}
                size={180}
                level="M"
                includeMargin={true}
              />
            </div>

              <div className="container text-center mt-4">
                {/* Labels Row */}
                <div className="row fw-bold mb-1">
                  <div className="col-6">Date</div>
                  <div className="col-6">Time</div>
                </div>

                {/* Values Row */}
                <div className="row">
                  <div className="col-6">
                    {new Date(selectedEvent.startDate).toLocaleDateString()}
                  </div>
                  <div className="col-6 p-0">
                    {new Date(selectedEvent.startDate).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {selectedEvent.endDate
                      ? ` to ${new Date(selectedEvent.endDate).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeQrModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default Eventindex;
